import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle, ChevronLeft, Pause, Play, RefreshCw, Trash2,
} from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusDot } from "@/components/device/StatusDot";
import { VideoTile } from "@/components/video/VideoTile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, formatApiError } from "@/lib/api";
import { useRealtime } from "@/hooks/useRealtime";
import { toast } from "sonner";
import dayjs from "dayjs";

export default function DeviceDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [rotatedToken, setRotatedToken] = useState(null);
  const { onlineDeviceIds } = useRealtime(true);

  const load = async () => {
    try {
      const r = await api.get(`/devices/${id}`);
      setDevice(r.data);
      setNewName(r.data.name);
    } catch (e) {
      toast.error(formatApiError(e));
      nav("/devices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading || !device) {
    return <div className="px-6 py-6 overline animate-pulse" data-testid="device-detail-loading">CHARGEMENT...</div>;
  }

  const status = onlineDeviceIds.includes(device.id) ? "online" : device.status;
  const merged = { ...device, status };

  const rename = async () => {
    if (newName === device.name) return;
    try {
      const r = await api.patch(`/devices/${device.id}`, { name: newName });
      setDevice(r.data); toast.success("Nom mis à jour");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const rotate = async () => {
    try {
      const r = await api.post(`/devices/${device.id}/rotate-token`);
      setRotatedToken(r.data); toast.success("Jeton renouvelé");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleStatus = async () => {
    const action = device.status === "disabled" ? "enable" : "disable";
    try {
      const r = await api.post(`/devices/${device.id}/${action}`);
      setDevice(r.data); toast.success(action === "enable" ? "Appareil activé" : "Appareil désactivé");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async () => {
    try {
      await api.delete(`/devices/${device.id}`);
      toast.success("Appareil supprimé"); nav("/devices");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="device-detail-page">
      <PageHeader
        overline={`APPAREIL · ${merged.id.slice(0, 8).toUpperCase()}`}
        title={
          <span className="flex items-center gap-3">
            <StatusDot status={status} />
            {merged.name}
          </span>
        }
        description={`${merged.type} · ${merged.os} · agent ${merged.agent_version || "non connecté"}`}
        actions={
          <Button variant="outline" onClick={() => nav("/devices")} className="rounded-none border-edge bg-transparent" data-testid="device-back">
            <ChevronLeft className="size-3.5 mr-1.5" strokeWidth={1.5} /> RETOUR
          </Button>
        }
      />

      <div className="px-6 py-6 grid lg:grid-cols-3 gap-6">
        {/* Video preview */}
        <div className="lg:col-span-2 space-y-4">
          <div className="overline">FLUX EN DIRECT</div>
          <VideoTile device={merged} intent="detail" siblingCount={1} />

          <div className="overline pt-4">CAPACITÉS ANNONCÉES</div>
          {merged.capabilities.length === 0 ? (
            <div className="panel rounded-none p-4 text-xs text-zinc-500">Aucune capacité annoncée. L'agent n'est pas encore connecté.</div>
          ) : (
            <div className="panel rounded-none divide-y divide-edge">
              {merged.capabilities.map((c) => (
                <div key={c.id} className="px-4 py-3 flex items-center justify-between" data-testid={`capability-${c.id}`}>
                  <div>
                    <div className="text-sm">{c.label}</div>
                    <div className="overline mt-0.5">{c.kind} · {c.subkind}</div>
                  </div>
                  <span className={`text-xs font-mono ${c.enabled ? "text-status-online" : "text-zinc-500"}`}>
                    {c.enabled ? "ACTIVE" : "INACTIVE"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions panel */}
        <div className="space-y-4">
          <div className="panel rounded-none">
            <div className="border-b border-edge px-4 py-2 overline">RENOMMER</div>
            <div className="p-4 flex gap-2">
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} className="rounded-none bg-obsidian border-edge font-mono text-sm" data-testid="device-rename-input" />
              <Button onClick={rename} className="rounded-none bg-brand hover:bg-brand-hover" data-testid="device-rename-submit">OK</Button>
            </div>
          </div>

          <div className="panel rounded-none">
            <div className="border-b border-edge px-4 py-2 overline">INFOS</div>
            <div className="p-4 space-y-2 text-xs font-mono">
              <Row k="ID" v={merged.id} />
              <Row k="Créé le" v={dayjs(merged.created_at).format("YYYY-MM-DD HH:mm")} />
              <Row k="Dernière conn." v={merged.last_connected_at ? dayjs(merged.last_connected_at).format("YYYY-MM-DD HH:mm") : "—"} />
              <Row k="Dernier signal" v={merged.last_seen_at ? dayjs(merged.last_seen_at).format("YYYY-MM-DD HH:mm:ss") : "—"} />
              <Row k="IP" v={merged.network?.ip || "—"} />
            </div>
          </div>

          <div className="panel rounded-none">
            <div className="border-b border-edge px-4 py-2 overline">ACTIONS</div>
            <div className="p-4 space-y-2">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="w-full justify-start rounded-none border-edge bg-transparent" data-testid="device-rotate">
                    <RefreshCw className="size-3.5 mr-2" strokeWidth={1.5} /> Régénérer le jeton
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-none bg-panel border-edge">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-display">Régénérer le jeton ?</AlertDialogTitle>
                    <AlertDialogDescription className="text-xs text-zinc-500">L'agent en cours sera déconnecté et devra être reconfiguré avec le nouveau jeton.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-none border-edge bg-transparent">Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={rotate} className="rounded-none bg-brand hover:bg-brand-hover" data-testid="device-rotate-confirm">Confirmer</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <Button variant="outline" onClick={toggleStatus} className="w-full justify-start rounded-none border-edge bg-transparent" data-testid="device-toggle">
                {device.status === "disabled" ? <><Play className="size-3.5 mr-2" strokeWidth={1.5} /> Réactiver</> : <><Pause className="size-3.5 mr-2" strokeWidth={1.5} /> Désactiver</>}
              </Button>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="w-full justify-start rounded-none border-status-offline/40 text-status-offline bg-transparent hover:bg-status-offline/10" data-testid="device-delete">
                    <Trash2 className="size-3.5 mr-2" strokeWidth={1.5} /> Supprimer
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-none bg-panel border-edge">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-display flex items-center gap-2">
                      <AlertTriangle className="size-5 text-status-offline" /> Supprimer cet appareil ?
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-xs text-zinc-500">L'appareil sera retiré de la plateforme et son agent ne pourra plus se reconnecter.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-none border-edge bg-transparent">Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={remove} className="rounded-none bg-status-offline hover:bg-status-offline/80" data-testid="device-delete-confirm">Supprimer</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>
      </div>

      {rotatedToken && (
        <div className="px-6 pb-8">
          <div className="panel rounded-none border-status-pending">
            <div className="border-b border-edge px-4 py-2 overline text-status-pending">⚠ NOUVEAU JETON — VISIBLE UNE SEULE FOIS</div>
            <pre className="px-4 py-3 text-xs font-mono overflow-auto text-zinc-200" data-testid="device-rotated-token">{JSON.stringify(rotatedToken.config, null, 2)}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-zinc-500 uppercase tracking-wider text-[10px]">{k}</span>
      <span className="text-zinc-200 truncate">{v}</span>
    </div>
  );
}
